import { useQuery, UseQueryResult } from "@tanstack/react-query";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";

export type FieldLocation = "URL" | "BODY" | "HEADER";

export interface ICurrencyProviderRequestFieldItem {
  name: string | null;
  location: FieldLocation;
}

export interface ICurrencyProviderRequestField {
  apiKey: ICurrencyProviderRequestFieldItem;
  username: ICurrencyProviderRequestFieldItem;
  password: ICurrencyProviderRequestFieldItem;
  baseCurrency: ICurrencyProviderRequestFieldItem;
  targetCurrency: ICurrencyProviderRequestFieldItem;
  httpMethod: "GET" | "POST";
  contentType: string;
}

export interface ICurrencyProviderResponseField {
  ratio: string | null;
}

export interface ICurrencyProvider {
  id: number;
  name: string;
  baseUrl?: string | null;
  apiKey?: string | null;
  username?: string | null;
  password?: string | null;
  requestField?: ICurrencyProviderRequestField;
  responseField?: ICurrencyProviderResponseField;
  updateIntervalSeconds?: number;
  isDeleted?: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdDate?: string;
  timestamp?: string;
  version?: number;
}

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export const fetchCurrencyProviders = async (
  params: Params<ICurrencyProvider>
): Promise<IPageable<ICurrencyProvider>> => {
  try {
    const response = await axiosInstance.get("/currency-providers", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the currency providers."
    );
  }
};

export const fetchAllCurrencyProviders = async (): Promise<ICurrencyProvider[]> => {
  try {
    const response = await axiosInstance.get("/currency-providers/all", {
      params: { isDeleted: false },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching all currency providers."
    );
  }
};

export const fetchCurrencyProvider = async (
  id: ICurrencyProvider["id"]
): Promise<ICurrencyProvider> => {
  try {
    const response = await axiosInstance.get(`/currency-providers/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while fetching currency provider ${id}.`
    );
  }
};

export const addCurrencyProviderApi = async (
  data: Partial<ICurrencyProvider>
): Promise<ICurrencyProvider> => {
  try {
    const response = await axiosInstance.post("/currency-providers", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the currency provider."
    );
  }
};

export const editCurrencyProviderApi = async (
  id: number | string,
  data: Partial<ICurrencyProvider>
): Promise<ICurrencyProvider> => {
  try {
    const response = await axiosInstance.put(`/currency-providers/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the currency provider."
    );
  }
};

export const deleteCurrencyProviderApi = async (
  id: number | string
): Promise<void> => {
  try {
    await axiosInstance.delete(`/currency-providers/${id}`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the currency provider."
    );
  }
};

export const useCurrencyProvidersPageQuery = (
  params: Params<ICurrencyProvider>
): UseQueryResult<IPageable<ICurrencyProvider>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
    isDeleted: false,
  };
  const queryKey = ["currencyProvidersPage", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchCurrencyProviders(effectiveParams),
  });
};
